import React, { useState, useEffect } from "react";
import axios from "axios";
import cookie from "react-cookies";
import Bugsnag from "@bugsnag/js";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Alert,
  Spinner,
} from "reactstrap";
import constants from "../../Constants";

const baseURL = constants.baseURL;

// Star component for rating display
const Star = ({ filled, hovered, onClick, onMouseEnter, onMouseLeave }) => (
  <span
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      cursor: "pointer",
      fontSize: "32px",
      color: filled || hovered ? "#FFD700" : "#E0E0E0",
      transition: "color 0.2s ease, transform 0.1s ease",
      display: "inline-block",
      transform: hovered ? "scale(1.1)" : "scale(1)",
      marginRight: "4px",
    }}
  >
    ★
  </span>
);

// Display only stars (no interaction)
const StarDisplay = ({ rating, size = 24 }) => (
  <span>
    {[1, 2, 3, 4, 5].map((star) => (
      <span
        key={star}
        style={{
          fontSize: `${size}px`,
          color: star <= rating ? "#FFD700" : "#E0E0E0",
          marginRight: "2px",
        }}
      >
        ★
      </span>
    ))}
  </span>
);

const DriverRating = ({ 
  orderid, 
  driverfirstname, 
  driverlastname,
  orderstatus,
  onRatingSubmitted 
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingRating, setCheckingRating] = useState(true);
  const [existingRating, setExistingRating] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Check if order is already rated on mount
  useEffect(() => {
    const checkExistingRating = async () => {
      if (!orderid) {
        setCheckingRating(false);
        return;
      }

      try {
        const response = await axios.get(`${baseURL}/customer-driver-rating`, {
          params: { orderid },
        });

        if (response.data.rated) {
          setExistingRating(response.data.rating);
        }
      } catch (err) {
        console.log("Error checking rating:", err);
      } finally {
        setCheckingRating(false);
      }
    };

    checkExistingRating();
  }, [orderid]);

  const handleSubmitRating = async () => {
    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    setLoading(true);
    setError("");

    const token = cookie.load("usr-id-qt") || cookie.load("quotetoken");

    try {
      const response = await axios.post(
        `${baseURL}/customer-rate-driver`,
        {
          orderid,
          rating,
          comment: comment.trim() || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setExistingRating(response.data.rating);
        if (onRatingSubmitted) {
          onRatingSubmitted(response.data.rating);
        }
      }
    } catch (err) {
      console.error("Error submitting rating:", err);
      Bugsnag.notify(err);
      setError(
        err.response?.data?.error || "Failed to submit rating. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Only show for completed/finished orders
  if (!["finished", "completed"].includes(orderstatus)) {
    return null;
  }

  // Loading state
  if (checkingRating) {
    return (
      <div className="driver-rating-container" style={styles.container}>
        <Spinner size="sm" color="primary" /> Checking rating status...
      </div>
    );
  }

  // Already rated - show the existing rating
  if (existingRating) {
    return (
      <div className="driver-rating-container" style={styles.container}>
        <div style={styles.header}>
          <h5 style={styles.title}>Your Rating</h5>
        </div>
        <div style={styles.ratedDisplay}>
          <StarDisplay rating={existingRating.rating} size={28} />
          <span style={styles.ratingText}>
            {existingRating.rating}/5
          </span>
        </div>
        {existingRating.comment && (
          <div style={styles.existingComment}>
            "{existingRating.comment}"
          </div>
        )}
        <Alert color="success" style={{ marginTop: "10px" }}>
          Thank you for rating your trip with {driverfirstname || "the driver"}!
        </Alert>
      </div>
    );
  }

  // Success state after just submitting
  if (success) {
    return (
      <div className="driver-rating-container" style={styles.container}>
        <Alert color="success">
          <h5>🎉 Thank you for your feedback!</h5>
          <p>Your rating helps us improve our service.</p>
        </Alert>
      </div>
    );
  }

  // Rating form
  return (
    <div className="driver-rating-container" style={styles.container}>
      <div style={styles.header}>
        <h5 style={styles.title}>Rate Your Trip</h5>
        {driverfirstname && (
          <p style={styles.subtitle}>
            How was your experience with {driverfirstname} {driverlastname || ""}?
          </p>
        )}
      </div>

      {error && (
        <Alert color="danger" style={{ marginBottom: "15px" }}>
          {error}
        </Alert>
      )}

      <div style={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            filled={star <= rating}
            hovered={star <= hoveredRating}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
          />
        ))}
        {rating > 0 && (
          <span style={styles.ratingLabel}>
            {getRatingLabel(rating)}
          </span>
        )}
      </div>

      <FormGroup style={{ marginTop: "15px" }}>
        <Label for="ratingComment" style={styles.label}>
          Leave a comment (optional)
        </Label>
        <Input
          type="textarea"
          id="ratingComment"
          placeholder="Tell us about your experience..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={500}
          rows={3}
          style={styles.textarea}
        />
        <small style={styles.charCount}>
          {comment.length}/500 characters
        </small>
      </FormGroup>

      <Button
        color="primary"
        onClick={handleSubmitRating}
        disabled={loading || rating === 0}
        style={styles.submitButton}
      >
        {loading ? (
          <>
            <Spinner size="sm" /> Submitting...
          </>
        ) : (
          <>
            Submit Rating
            <i className="mdi mdi-star ml-2"></i>
          </>
        )}
      </Button>
    </div>
  );
};

// Helper function to get rating label
const getRatingLabel = (rating) => {
  const labels = {
    1: "Poor 😞",
    2: "Fair 😐",
    3: "Good 🙂",
    4: "Very Good 😊",
    5: "Excellent! 🌟",
  };
  return labels[rating] || "";
};

// Styles
const styles = {
  container: {
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    marginTop: "20px",
    marginBottom: "20px",
    border: "1px solid #e9ecef",
  },
  header: {
    textAlign: "center",
    marginBottom: "15px",
  },
  title: {
    color: "#333",
    marginBottom: "5px",
    fontWeight: "600",
  },
  subtitle: {
    color: "#666",
    fontSize: "14px",
    margin: 0,
  },
  starsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
  },
  ratingLabel: {
    marginLeft: "10px",
    fontSize: "16px",
    fontWeight: "500",
    color: "#666",
  },
  label: {
    fontWeight: "500",
    color: "#555",
  },
  textarea: {
    resize: "none",
    borderRadius: "8px",
  },
  charCount: {
    color: "#999",
    fontSize: "12px",
    float: "right",
    marginTop: "4px",
  },
  submitButton: {
    width: "100%",
    marginTop: "15px",
    padding: "12px",
    fontSize: "16px",
    fontWeight: "500",
  },
  ratedDisplay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  ratingText: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  existingComment: {
    textAlign: "center",
    fontStyle: "italic",
    color: "#666",
    padding: "10px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginTop: "10px",
  },
};

export { DriverRating, StarDisplay };
export default DriverRating;
